import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StatsCounter from '../components/StatsCounter/StatsCounter';
import SEO from '../components/SEO/SEO';
import StructuredData from '../components/StructuredData/StructuredData';
import styles from './AboutPage.module.css';

const AboutPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={styles.aboutPage}>
      <SEO 
        title="About SolveDot Labs | Software Development Company in Greater Noida, India"
        description="Learn about SolveDot Labs (SolveDot Labs Pvt. Ltd.) - a leading software development company specializing in AI solutions, website development, IoT systems, robotics, and custom software development. Based in Greater Noida, India. 200+ clients, 99% uptime guarantee."
        keywords="solve, solvedot, solvedotlabs, solvedot labs, solve dot labs, solvedot labs pvt ltd, SolveDot Labs, About SolveDot Labs, Software Development Company, Greater Noida, India Software Company, Tech Company, Software Solutions Provider, AI Solutions, Website Development, IoT, Robotics"
      />
      <StructuredData type="about" />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      {/* Enhanced Hero Header */}
      <section className={styles.heroHeader}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>About Us</span>
          <h1 className={styles.heroTitle}>
            We're Not Just a Vendor.
            <br />
            <span className={styles.titleHighlight}>We're Your Co-Pilot in Innovation.</span>
          </h1>
          <p className={styles.heroDescription}>
            SolveDot Labs was founded on a simple belief: <br />
            Technology should be an opportunity, not a problem. We are a collective of expert engineers, creative designers, and strategic thinkers obsessed with one thing—building software that delivers tangible, game-changing results for our clients.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className={styles.missionSection}>
        <div className={styles.missionContent}>
          <div className={styles.missionText}>
            <span className={styles.sectionBadge}>Our Mission</span>
            <h2 className={styles.sectionTitle}>
              Transforming Vision Into Reality
            </h2>
            <p className={styles.missionDescription}>
              At SolveDot Labs, we don't just write code—we architect solutions that drive business growth. Every project is an opportunity to push boundaries, challenge conventions, and create something extraordinary. We combine cutting-edge technology with strategic thinking to deliver software that doesn't just work—it transforms.
            </p>
            <div className={styles.missionStats}>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>🚀</div>
                <StatsCounter end={20} suffix="+" />
                <div className={styles.statLabel}>Projects Delivered</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>😊</div>
                <StatsCounter end={20} suffix="+" />
                <div className={styles.statLabel}>Happy Clients</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>⭐</div>
                <StatsCounter end={4} suffix="+" />
                <div className={styles.statLabel}>Years of Excellence</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>⚡</div>
                <StatsCounter end={99} suffix="%" />
                <div className={styles.statLabel}>Uptime Guarantee</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className={styles.promiseSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Promise</span>
          <h2 className={styles.sectionTitle}>Our Unwavering Guarantee</h2>
          <p className={styles.sectionDescription}>
            These aren't just words—they're commitments we live by every single day.
          </p>
        </div>
        <div className={styles.promiseCards}>
          <div className={styles.promiseCard}>
            <div className={styles.cardIconWrapper}>
              <div className={styles.cardIconGlow}></div>
              <div className={styles.cardIcon}>✨</div>
            </div>
            <h3 className={styles.cardTitle}>Quality Commitment</h3>
            <p className={styles.cardDescription}>
              Every line of code is meticulously crafted, tested, and reviewed to ensure robust, scalable, and secure solutions. We don't just build software; we build confidence.
            </p>
            <div className={styles.cardFooter}>
              <span className={styles.cardLink}>Learn More <span className={styles.arrow}>→</span></span>
            </div>
          </div>
          <div className={styles.promiseCard}>
            <div className={styles.cardIconWrapper}>
              <div className={styles.cardIconGlow}></div>
              <div className={styles.cardIcon}>⚡</div>
            </div>
            <h3 className={styles.cardTitle}>Almost Zero Downtime Philosophy</h3>
            <p className={styles.cardDescription}>
              In today's fast-paced world, there's no room for interruption. Our architecture is designed for high availability, guaranteeing your operations run smoothly 24/7.
            </p>
            <div className={styles.cardFooter}>
              <span className={styles.cardLink}>Learn More <span className={styles.arrow}>→</span></span>
            </div>
          </div>
          <div className={styles.promiseCard}>
            <div className={styles.cardIconWrapper}>
              <div className={styles.cardIconGlow}></div>
              <div className={styles.cardIcon}>🎯</div>
            </div>
            <h3 className={styles.cardTitle}>Guaranteed Results</h3>
            <p className={styles.cardDescription}>
              We stand by our work. Our solutions come with a guarantee to perform as promised, ensuring you get the value and ROI you expect from your investment.
            </p>
            <div className={styles.cardFooter}>
              <span className={styles.cardLink}>Learn More <span className={styles.arrow}>→</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Expertise Section */}
      <section className={styles.expertiseSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Expertise</span>
          <h2 className={styles.sectionTitle}>Areas We Excel In</h2>
          <p className={styles.sectionDescription}>
            From cutting-edge AI to robust manufacturing systems, we deliver solutions across diverse industries and technologies.
          </p>
        </div>
        <div className={styles.expertiseGrid}>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>🤖</div>
            <h3>AI & Machine Learning</h3>
            <p>Intelligent automation, predictive analytics, and AI-powered solutions that transform business operations.</p>
          </div>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>🏭</div>
            <h3>Manufacturing & Industry 4.0</h3>
            <p>Smart factory solutions, MES systems, and IoT integration for modern manufacturing excellence.</p>
          </div>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>💻</div>
            <h3>Web & Mobile Development</h3>
            <p>Scalable web applications and native mobile apps built with modern frameworks and best practices.</p>
          </div>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>☁️</div>
            <h3>Cloud & DevOps</h3>
            <p>Cloud infrastructure, CI/CD pipelines, and automated deployment strategies for seamless operations.</p>
          </div>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>🔒</div>
            <h3>Cybersecurity</h3>
            <p>Enterprise-grade security solutions, threat monitoring, and compliance management for protected operations.</p>
          </div>
          <div className={styles.expertiseCard}>
            <div className={styles.expertiseIcon}>📊</div>
            <h3>Data Analytics</h3>
            <p>Business intelligence, data visualization, and actionable insights from your data assets.</p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className={styles.valuesSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Values</span>
          <h2 className={styles.sectionTitle}>What Drives Us</h2>
        </div>
        <div className={styles.valuesGrid}>
          <div className={styles.valueItem}>
            <div className={styles.valueIcon}>💡</div>
            <h3>Innovation First</h3>
            <p>We stay ahead of the curve, constantly exploring new technologies and methodologies to deliver cutting-edge solutions.</p>
          </div>
          <div className={styles.valueItem}>
            <div className={styles.valueIcon}>🤝</div>
            <h3>Partnership Approach</h3>
            <p>Your success is our success. We work as an extension of your team, deeply understanding your goals and challenges.</p>
          </div>
          <div className={styles.valueItem}>
            <div className={styles.valueIcon}>🔒</div>
            <h3>Security & Trust</h3>
            <p>We build with security at the core, ensuring your data and operations are protected with enterprise-grade safeguards.</p>
          </div>
          <div className={styles.valueItem}>
            <div className={styles.valueIcon}>📈</div>
            <h3>Results-Driven</h3>
            <p>Every solution is designed with measurable outcomes in mind. We track, analyze, and optimize for continuous improvement.</p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className={styles.teamSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Team</span>
          <h2 className={styles.sectionTitle}>Meet the Experts</h2>
          <p className={styles.sectionDescription}>
            A diverse team of passionate professionals dedicated to delivering excellence in every project.
          </p>
        </div>
        <div className={styles.teamGrid}>
          <div className={styles.teamCard}>
            <div className={styles.teamAvatar}>
              <div className={styles.avatarIcon}>👨‍💼</div>
            </div>
            <h3>Engineering Leaders</h3>
            <p className={styles.teamRole}>Senior Architects & Tech Leads</p>
            <p className={styles.teamDescription}>
              Seasoned professionals with 10+ years of experience in designing scalable, enterprise-grade solutions.
            </p>
          </div>
          <div className={styles.teamCard}>
            <div className={styles.teamAvatar}>
              <div className={styles.avatarIcon}>👩‍💻</div>
            </div>
            <h3>Development Team</h3>
            <p className={styles.teamRole}>Full-Stack Developers</p>
            <p className={styles.teamDescription}>
              Expert developers proficient in modern technologies, delivering clean, maintainable, and performant code.
            </p>
          </div>
          <div className={styles.teamCard}>
            <div className={styles.teamAvatar}>
              <div className={styles.avatarIcon}>🎨</div>
            </div>
            <h3>Design & UX</h3>
            <p className={styles.teamRole}>UI/UX Designers</p>
            <p className={styles.teamDescription}>
              Creative minds crafting intuitive, user-centric designs that enhance user experience and drive engagement.
            </p>
          </div>
          <div className={styles.teamCard}>
            <div className={styles.teamAvatar}>
              <div className={styles.avatarIcon}>🔬</div>
            </div>
            <h3>Quality Assurance</h3>
            <p className={styles.teamRole}>QA Engineers</p>
            <p className={styles.teamDescription}>
              Meticulous testers ensuring flawless functionality, performance, and security across all our solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className={styles.processSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Our Process</span>
          <h2 className={styles.sectionTitle}>How We Work</h2>
          <p className={styles.sectionDescription}>
            A proven methodology that ensures successful project delivery from concept to deployment.
          </p>
        </div>
        <div className={styles.processSteps}>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>01</div>
            <h3>Discovery & Planning</h3>
            <p>We dive deep into your business needs, challenges, and goals to create a comprehensive project roadmap.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>02</div>
            <h3>Design & Architecture</h3>
            <p>Our architects design scalable, secure solutions tailored to your specific requirements and future growth.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>03</div>
            <h3>Development & Testing</h3>
            <p>Agile development with continuous testing ensures high-quality code and early issue detection.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>04</div>
            <h3>Deployment & Support</h3>
            <p>Smooth deployment with comprehensive documentation and ongoing support to ensure your success.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Business?</h2>
          <p className={styles.ctaDescription}>
            Let's discuss how we can help you achieve your goals with technology that truly makes a difference.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/contact" className={styles.primaryCta}>
              <span>Get in Touch</span>
              <span className={styles.arrow}>→</span>
            </Link>
            <Link to="/services" className={styles.secondaryCta}>
              Explore Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
